'use client';
import React, { useState, useEffect, useCallback } from 'react';

// ============ Types ============
interface Project {
  id: string;
  name: string;
  target: number | null;
  unitPrice: number | null;
  due: number | null;
}

interface Supervisor {
  name: string;
  role: string;
  contact: string;
}

interface Resident {
  apt: number;
  name: string;
  phone: string;
  registeredAt: string;
}

interface Contribution {
  id: number;
  name: string;
  apt: number;
  projectId: string;
  amount: number;
  status: 'paid' | 'partial' | 'unpaid';
  qty: number | null;
}

interface BuildingData {
  apartments: number;
  projects: Project[];
  supervisors: Supervisor[];
  residents: Resident[];
  contributions: Contribution[];
}

interface DataStore {
  [key: string]: BuildingData;
}

interface ActivityItem {
  icon: string;
  text: string;
  time: Date;
}

type PageId = 'dashboard' | 'buildings' | 'residents' | 'projects' | 'contributions' | 'expenses' | 'maintenance' | 'announcements' | 'reports' | 'settings';

// ============ Initial Data ============
const initialData: DataStore = {
  A1: {
    apartments: 32,
    projects: [
      { id: 'aman', name: 'خدمة الأمن', target: 32000, unitPrice: null, due: 1000 },
      { id: 'gaz', name: 'قفص عداد الغاز', target: 32000, unitPrice: null, due: 1000 },
      { id: 'bab', name: 'شريحة باب المدخل', target: null, unitPrice: null, due: null }
    ],
    supervisors: [{ name: 'عطاء الله إبراهيم', role: 'مدير عام', contact: '—' }],
    residents: [
      { apt: 6, name: 'ARAB NOUREDDINE', phone: '—', registeredAt: '—' },
      { apt: 11, name: 'DJAMEL TRIQUE', phone: '—', registeredAt: '—' },
      { apt: 15, name: 'BEN AMMAR HAMZA', phone: '—', registeredAt: '—' },
      { apt: 24, name: 'MAHFOUD MERAOUBI', phone: '—', registeredAt: '—' },
      { apt: 28, name: 'BOUYAKOUB ABDELHALIM', phone: '—', registeredAt: '—' },
      { apt: 30, name: 'AMIR BENREZKALLAH', phone: '—', registeredAt: '—' },
      { apt: 32, name: 'AMAR MED AMINE', phone: '—', registeredAt: '—' }
    ],
    contributions: [
      { id: 1000, name: 'ARAB NOUREDDINE', apt: 6, projectId: 'aman', amount: 1000, status: 'paid', qty: null },
      { id: 1001, name: 'ARAB NOUREDDINE', apt: 6, projectId: 'gaz', amount: 1000, status: 'paid', qty: null },
      { id: 1002, name: 'ARAB NOUREDDINE', apt: 6, projectId: 'bab', amount: 600, status: 'paid', qty: null },
      { id: 1003, name: 'DJAMEL TRIQUE', apt: 11, projectId: 'aman', amount: 1000, status: 'paid', qty: null },
      { id: 1004, name: 'DJAMEL TRIQUE', apt: 11, projectId: 'gaz', amount: 1000, status: 'paid', qty: null },
      { id: 1005, name: 'DJAMEL TRIQUE', apt: 11, projectId: 'bab', amount: 300, status: 'paid', qty: null },
      { id: 1006, name: 'BEN AMMAR HAMZA', apt: 15, projectId: 'aman', amount: 1000, status: 'paid', qty: null },
      { id: 1007, name: 'BEN AMMAR HAMZA', apt: 15, projectId: 'gaz', amount: 1000, status: 'paid', qty: null },
      { id: 1008, name: 'BEN AMMAR HAMZA', apt: 15, projectId: 'bab', amount: 300, status: 'paid', qty: null },
      { id: 1009, name: 'MAHFOUD MERAOUBI', apt: 24, projectId: 'aman', amount: 1000, status: 'paid', qty: null },
      { id: 1010, name: 'MAHFOUD MERAOUBI', apt: 24, projectId: 'gaz', amount: 1000, status: 'paid', qty: null },
      { id: 1011, name: 'MAHFOUD MERAOUBI', apt: 24, projectId: 'bab', amount: 900, status: 'paid', qty: null },
      { id: 1012, name: 'BOUYAKOUB ABDELHALIM', apt: 28, projectId: 'aman', amount: 1000, status: 'paid', qty: null },
      { id: 1013, name: 'BOUYAKOUB ABDELHALIM', apt: 28, projectId: 'gaz', amount: 1000, status: 'paid', qty: null },
      { id: 1014, name: 'BOUYAKOUB ABDELHALIM', apt: 28, projectId: 'bab', amount: 0, status: 'unpaid', qty: null },
      { id: 1015, name: 'AMIR BENREZKALLAH', apt: 30, projectId: 'aman', amount: 1000, status: 'paid', qty: null },
      { id: 1016, name: 'AMIR BENREZKALLAH', apt: 30, projectId: 'gaz', amount: 1000, status: 'paid', qty: null },
      { id: 1017, name: 'AMIR BENREZKALLAH', apt: 30, projectId: 'bab', amount: 300, status: 'paid', qty: null },
      { id: 1018, name: 'AMAR MED AMINE', apt: 32, projectId: 'aman', amount: 1000, status: 'paid', qty: null },
      { id: 1019, name: 'AMAR MED AMINE', apt: 32, projectId: 'gaz', amount: 1000, status: 'paid', qty: null },
      { id: 1020, name: 'AMAR MED AMINE', apt: 32, projectId: 'bab', amount: 300, status: 'paid', qty: null }
    ]
  }
};

const fmt = (n: number) => n.toLocaleString('en-US');
const statusLabel: Record<string, string> = { paid: 'مسدد', partial: 'جزئي', unpaid: 'غير مسدد' };

export default function HomePage() {
  const [data, setData] = useState<DataStore>(initialData);
  const [current, setCurrent] = useState('A1');
  const [activePage, setActivePage] = useState<PageId>('dashboard');
  const [doorsFilter, setDoorsFilter] = useState<'all' | 'registered' | 'unregistered'>('all');
  const [doorSearch, setDoorSearch] = useState('');
  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [uidCounter, setUidCounter] = useState(2000);
  const [currentDate, setCurrentDate] = useState('');

  // Modals
  const [modalBuilding, setModalBuilding] = useState(false);
  const [modalProject, setModalProject] = useState(false);
  const [modalSupervisor, setModalSupervisor] = useState(false);
  const [modalContribution, setModalContribution] = useState(false);
  const [modalResident, setModalResident] = useState(false);
  const [modalResidentView, setModalResidentView] = useState(false);
  const [viewingApt, setViewingApt] = useState<number | null>(null);
  const [editingContribId, setEditingContribId] = useState<number | null>(null);
  const [residentModalTitle, setResidentModalTitle] = useState('تسجيل ساكن');

  // Form fields
  const [bName, setBName] = useState('');
  const [bApts, setBApts] = useState('');
  const [pName, setPName] = useState('');
  const [pTarget, setPTarget] = useState('');
  const [pUnitPrice, setPUnitPrice] = useState('');
  const [pDue, setPDue] = useState('');
  const [sName, setSName] = useState('');
  const [sRole, setSRole] = useState('مشرف عمارة');
  const [sContact, setSContact] = useState('');
  const [rName, setRName] = useState('');
  const [rApt, setRApt] = useState('');
  const [rPhone, setRPhone] = useState('');
  const [rDate, setRDate] = useState('');
  const [cResident, setCResident] = useState('');
  const [cProject, setCProject] = useState('');
  const [cQty, setCQty] = useState('1');
  const [cAmount, setCAmount] = useState('');
  const [cStatus, setCStatus] = useState<'paid' | 'partial' | 'unpaid'>('paid');
  const [cDueDisplay, setCDueDisplay] = useState('');
  const [cRemainingDisplay, setCRemainingDisplay] = useState('');
  const [cAmountLabel, setCAmountLabel] = useState('المبلغ (دج)');
  const [showQtyField, setShowQtyField] = useState(false);
  const [showDueField, setShowDueField] = useState(false);
  const [showRemainingField, setShowRemainingField] = useState(false);
  const [cUnitPriceLabel, setCUnitPriceLabel] = useState('0');
  const [contribModalTitle, setContribModalTitle] = useState('تسجيل مساهمة');

  useEffect(() => {
    const d = new Date();
    setCurrentDate(d.toLocaleDateString('ar-DZ', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }));
  }, []);

  const getBuilding = useCallback(() => data[current], [data, current]);

  const logActivity = useCallback((icon: string, text: string) => {
    setActivity(prev => {
      const next = [{ icon, text, time: new Date() }, ...prev];
      return next.slice(0, 8);
    });
  }, []);

  const summary = useCallback((key: string) => {
    const b = data[key];
    const participants = b.residents.length;
    const coverage = b.apartments ? participants / b.apartments * 100 : 0;
    const collected = b.contributions.reduce((s, c) => s + c.amount, 0);
    return { apartments: b.apartments, participants, coverage, collected };
  }, [data]);

  const showPage = (page: PageId) => {
    setActivePage(page);
    setSidebarOpen(false);
  };

  // ============ Building Modal ============
  const submitBuilding = () => {
    if (!bName.trim() || !bApts) { alert('أدخل اسم العمارة وعدد الشقق'); return; }
    if (data[bName.trim()]) { alert('هذه العمارة موجودة مسبقًا'); return; }
    const newData = { ...data };
    newData[bName.trim()] = { apartments: parseInt(bApts), projects: [], supervisors: [], residents: [], contributions: [] };
    setData(newData);
    setCurrent(bName.trim());
    logActivity('🏢', `تم إضافة عمارة جديدة: ${bName.trim()} (${bApts} شقة)`);
    setBName(''); setBApts('');
    setModalBuilding(false);
  };

  // ============ Project Modal ============
  const submitProject = () => {
    if (!pName.trim()) { alert('أدخل اسم المشروع'); return; }
    const newData = { ...data };
    const newId = 'p' + uidCounter;
    setUidCounter(prev => prev + 1);
    newData[current].projects = [...newData[current].projects, {
      id: newId,
      name: pName.trim(),
      target: pTarget ? parseFloat(pTarget) : null,
      unitPrice: pUnitPrice ? parseFloat(pUnitPrice) : null,
      due: pDue ? parseFloat(pDue) : null
    }];
    setData(newData);
    logActivity('📊', `تم إنشاء مشروع جديد: ${pName.trim()} — العمارة ${current}`);
    setPName(''); setPTarget(''); setPUnitPrice(''); setPDue('');
    setModalProject(false);
  };

  const deleteProject = (id: string) => {
    const b = data[current];
    const proj = b.projects.find(p => p.id === id);
    if (!proj) return;
    const linked = b.contributions.filter(c => c.projectId === id);
    const linkedAmount = linked.reduce((s, c) => s + c.amount, 0);
    const msg = linked.length
      ? `حذف مشروع «${proj.name}» سيحذف معه ${linked.length} مساهمة بقيمة ${fmt(linkedAmount)} دج. متابعة؟`
      : `حذف مشروع «${proj.name}»؟`;
    if (!confirm(msg)) return;
    const newData = { ...data };
    newData[current].projects = newData[current].projects.filter(p => p.id !== id);
    newData[current].contributions = newData[current].contributions.filter(c => c.projectId !== id);
    setData(newData);
  };

  // ============ Supervisor Modal ============
  const submitSupervisor = () => {
    if (!sName.trim()) { alert('أدخل اسم المشرف'); return; }
    const newData = { ...data };
    newData[current].supervisors = [...newData[current].supervisors, { name: sName.trim(), role: sRole, contact: sContact.trim() || '—' }];
    setData(newData);
    logActivity('🛡️', `تم إضافة مشرف جديد: ${sName.trim()}`);
    setSName(''); setSContact('');
    setModalSupervisor(false);
  };

  // ============ Resident Modal ============
  const openAddResident = (apt: number) => {
    setResidentModalTitle(`تسجيل ساكن — الشقة ${apt}`);
    setRName('');
    setRApt(String(apt));
    setRPhone('');
    const today = new Date();
    setRDate(today.toISOString().slice(0, 10));
    setModalResident(true);
  };

  const submitResident = () => {
    if (!rName.trim() || !rApt) { alert('أدخل اسم الساكن ورقم الشقة'); return; }
    const aptNum = parseInt(rApt);
    const newData = { ...data };
    const existing = newData[current].residents.find(r => r.apt === aptNum);
    if (existing) {
      existing.name = rName.trim();
      existing.phone = rPhone.trim() || '—';
      existing.registeredAt = rDate || '—';
    } else {
      newData[current].residents = [...newData[current].residents, { apt: aptNum, name: rName.trim(), phone: rPhone.trim() || '—', registeredAt: rDate || '—' }];
      logActivity('👤', `تم إضافة مقيم جديد: ${rName.trim()} — العمارة ${current}`);
    }
    setData(newData);
    setModalResident(false);
  };

  const deleteResident = (apt: number) => {
    const b = data[current];
    const r = b.residents.find(x => x.apt === apt);
    if (!r) return;
    const linked = b.contributions.filter(c => c.apt === apt);
    const linkedAmount = linked.reduce((s, c) => s + c.amount, 0);
    const msg = linked.length
      ? `حذف تسجيل «${r.name}» سيحذف معه ${linked.length} مساهمة بقيمة ${fmt(linkedAmount)} دج. متابعة؟`
      : `حذف تسجيل «${r.name}»؟`;
    if (!confirm(msg)) return;
    const newData = { ...data };
    newData[current].residents = newData[current].residents.filter(x => x.apt !== apt);
    newData[current].contributions = newData[current].contributions.filter(c => c.apt !== apt);
    setData(newData);
    setModalResidentView(false);
  };

  // ============ Contribution Modal ============
  const handleContribProjectChange = (projectId: string) => {
    setCProject(projectId);
    const b = getBuilding();
    const project = b.projects.find(p => p.id === projectId);
    if (project && project.unitPrice) {
      setShowQtyField(true);
      setCUnitPriceLabel(fmt(project.unitPrice));
      if (!cQty) setCQty('1');
      const qty = parseInt(cQty) || 1;
      setCAmount(String(project.unitPrice * qty));
    } else {
      setShowQtyField(false);
    }
    if (project && project.due) {
      setShowDueField(true);
      setCDueDisplay(fmt(project.due) + ' دج');
      setCAmountLabel('المبلغ المدفوع فعليًا (دج)');
    } else {
      setShowDueField(false);
      setShowRemainingField(false);
      setCAmountLabel('المبلغ (دج)');
    }
  };

  const handleContribAmountInput = (val: string) => {
    setCAmount(val);
    const b = getBuilding();
    const project = b.projects.find(p => p.id === cProject);
    if (!project || !project.due) { setShowRemainingField(false); return; }
    const paid = parseFloat(val) || 0;
    const remaining = project.due - paid;
    if (remaining > 0) {
      setShowRemainingField(true);
      setCRemainingDisplay(fmt(remaining) + ' دج');
      setCStatus(paid <= 0 ? 'unpaid' : 'partial');
    } else {
      setShowRemainingField(false);
      setCStatus('paid');
    }
  };

  const openAddContribution = () => {
    const b = getBuilding();
    if (!b.residents.length) { alert('سجّل ساكنًا واحدًا على الأقل أولًا من صفحة السكان قبل تسجيل مساهمة.'); return; }
    setEditingContribId(null);
    setContribModalTitle('تسجيل مساهمة');
    setCAmount('');
    setCQty('1');
    setCStatus('paid');
    const firstProject = b.projects[0];
    if (firstProject) {
      handleContribProjectChange(firstProject.id);
    }
    const firstResident = b.residents.slice().sort((a, c) => a.apt - c.apt)[0];
    if (firstResident) setCResident(String(firstResident.apt));
    setModalContribution(true);
  };

  const editContribution = (id: number) => {
    const b = getBuilding();
    const c = b.contributions.find(x => x.id === id);
    if (!c) return;
    setEditingContribId(id);
    setContribModalTitle('تعديل مساهمة');
    setCResident(String(c.apt));
    setCQty(String(c.qty || 1));
    setCStatus(c.status);
    handleContribProjectChange(c.projectId);
    setCAmount(String(c.amount));
    setModalContribution(true);
  };

  const deleteContribution = (id: number) => {
    const b = getBuilding();
    const c = b.contributions.find(x => x.id === id);
    if (!c) return;
    if (!confirm(`حذف مساهمة «${c.name}» بقيمة ${fmt(c.amount)} دج؟`)) return;
    const newData = { ...data };
    newData[current].contributions = newData[current].contributions.filter(x => x.id !== id);
    setData(newData);
  };

  const submitContribution = () => {
    let apt = parseInt(cResident) || 0;
    const projectId = cProject;
    const amount = parseFloat(cAmount) || 0;
    const b = getBuilding();
    const project = b.projects.find(p => p.id === projectId);
    const qty = (project && project.unitPrice) ? (parseInt(cQty) || 1) : null;
    const resident = b.residents.find(r => r.apt === apt);
    if (!apt || !projectId || !resident) { alert('اختر ساكنًا ومشروعًا صالحين'); return; }
    const name = resident.name;
    const newData = { ...data };
    if (editingContribId !== null) {
      const c = newData[current].contributions.find(x => x.id === editingContribId);
      if (c) { c.name = name; c.apt = apt; c.projectId = projectId; c.amount = amount; c.status = cStatus; c.qty = qty; }
      setEditingContribId(null);
    } else {
      const newId = uidCounter;
      setUidCounter(prev => prev + 1);
      newData[current].contributions = [...newData[current].contributions, { id: newId, name, apt, projectId, amount, status: cStatus, qty }];
      logActivity('💰', `تم استلام مساهمة ${fmt(amount)} دج من ${name} — ${project?.name || ''}`);
    }
    setData(newData);
    setModalContribution(false);
  };

  const keys = Object.keys(data);

  // ============ Render Helpers ============
  const renderDashboard = () => {
    let totalApt = 0, totalReg = 0, totalCollected = 0, totalProjects = 0;
    keys.forEach(k => {
      const s = summary(k);
      totalApt += s.apartments; totalReg += s.participants; totalCollected += s.collected;
      totalProjects += data[k].projects.length;
    });
    return { totalApt, totalReg, totalCollected, totalProjects };
  };

  const dashStats = renderDashboard();

  const getResidentByApt = (apt: number) => getBuilding().residents.find(r => r.apt === apt);

  const getDoorTiles = () => {
    const b = getBuilding();
    const tiles = [];
    for (let apt = 1; apt <= b.apartments; apt++) {
      const r = getResidentByApt(apt);
      const isReg = !!r;
      if (doorsFilter === 'registered' && !isReg) continue;
      if (doorsFilter === 'unregistered' && isReg) continue;
      if (doorSearch) {
        const hay = (String(apt) + ' ' + (r ? r.name.toLowerCase() : '')).toLowerCase();
        if (!hay.includes(doorSearch.toLowerCase())) continue;
      }
      tiles.push({ apt, r, isReg });
    }
    return tiles;
  };

  const doorTiles = getDoorTiles();
  const b = getBuilding();
  const registeredCount = b.residents.length;

  const getContribsSorted = () => b.contributions.slice().sort((a, c) => a.apt - c.apt);

  const getProjectsForSelect = () => b.projects;
  const getResidentsForSelect = () => b.residents.slice().sort((a, c) => a.apt - c.apt);

  const viewingResident = viewingApt !== null ? getResidentByApt(viewingApt) : null;
  const viewingContribs = viewingApt !== null ? b.contributions.filter(c => c.apt === viewingApt) : [];

  const notifCount = Math.min(9, activity.length);

  return (
    <>
      <style>{`
        * { box-sizing: border-box; }
        html, body { margin: 0; height: 100%; overflow-x: hidden; }
        body {
          font-family: 'Cairo', sans-serif;
          background: #f4f6fa;
          color: #152238;
          line-height: 1.7;
          display: flex;
          min-height: 100vh;
        }
        .sidebar {
          width: 250px; flex: 0 0 auto;
          background: linear-gradient(180deg, #0f2747, #0a1c33);
          color: #e9eefb; padding: 22px 18px;
          display: flex; flex-direction: column;
          position: sticky; top: 0; height: 100vh; overflow-y: auto;
        }
        .brand { text-align: center; padding-bottom: 18px; border-bottom: 1px solid rgba(255,255,255,.1); margin-bottom: 16px; }
        .brand .mark {
          width: 56px; height: 56px; margin: 0 auto 10px; border-radius: 16px;
          background: linear-gradient(135deg, #d4a853, #b78a3a);
          display: grid; place-items: center; font-size: 26px; color: #0f2747; font-weight: 800;
        }
        .brand h1 { margin: 0; font-size: 17px; font-weight: 800; color: #fff; }
        .brand p { margin: 2px 0 0; font-size: 11.5px; color: #d4a853; }
        .nav-group { margin-bottom: 10px; }
        .nav-item {
          display: flex; align-items: center; gap: 10px; padding: 11px 13px; border-radius: 10px;
          color: #c6d3ea; font-size: 13.5px; font-weight: 600; cursor: pointer; margin-bottom: 3px;
          transition: background .15s;
        }
        .nav-item .ic { font-size: 16px; width: 20px; text-align: center; }
        .nav-item:hover { background: rgba(255,255,255,.06); }
        .nav-item.active { background: linear-gradient(90deg, #d4a853, #b78a3a); color: #0f2747; font-weight: 800; }
        .nav-item.soon { opacity: .55; }
        .nav-item.soon .tag { margin-right: auto; font-size: 9.5px; background: rgba(255,255,255,.12); padding: 2px 6px; border-radius: 6px; }
        .sidebar-footer { margin-top: auto; padding-top: 16px; border-top: 1px solid rgba(255,255,255,.1); font-size: 11px; color: #7d8db0; text-align: center; }
        .main { flex: 1; min-width: 0; display: flex; flex-direction: column; }
        .topbar {
          background: #fff; border-bottom: 1px solid #e7ecf3; padding: 14px 26px;
          display: flex; align-items: center; gap: 14px; flex-wrap: wrap; row-gap: 10px;
          position: sticky; top: 0; z-index: 5;
        }
        .hamburger { display: none; font-size: 20px; background: none; border: none; cursor: pointer; color: #0f2747; padding: 4px; }
        .topbar .date { font-size: 13px; color: #7286a3; white-space: nowrap; display: flex; align-items: center; gap: 6px; }
        .topbar .search {
          flex: 1 1 160px; min-width: 120px; max-width: 420px; background: #f4f6fa;
          border: 1px solid #e7ecf3; border-radius: 10px;
          padding: 9px 14px; font-size: 13px; color: #7286a3; font-family: 'Cairo', sans-serif;
        }
        .topbar .spacer { margin-right: auto; }
        .topbar .bell { font-size: 17px; position: relative; flex: 0 0 auto; }
        .topbar .bell .dot {
          position: absolute; top: -4px; left: -6px; background: #ef4444; color: #fff;
          font-size: 9px; border-radius: 50%; width: 15px; height: 15px;
          display: grid; place-items: center; font-weight: 700;
        }
        .topbar .user { display: flex; align-items: center; gap: 8px; flex: 0 0 auto; }
        .topbar .user .av {
          width: 34px; height: 34px; border-radius: 50%; background: #d4a853; color: #0f2747;
          display: grid; place-items: center; font-weight: 800; font-size: 13px; flex: 0 0 auto;
        }
        .topbar .user .name { font-size: 12.5px; font-weight: 700; white-space: nowrap; }
        .topbar .user .role { font-size: 10.5px; color: #7286a3; white-space: nowrap; }
        .content { padding: 26px; max-width: 1280px; width: 100%; margin: 0 auto; }
        .page { display: none; }
        .page.active { display: block; }
        .page-head { margin-bottom: 20px; }
        .page-head h2 { margin: 0; font-size: 22px; font-weight: 800; }
        .page-head p { margin: 4px 0 0; color: #7286a3; font-size: 13px; }
        .stat-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); gap: 14px; margin-bottom: 22px; }
        .stat-card { background: #fff; border: 1px solid #e7ecf3; border-radius: 14px; padding: 16px 18px; display: flex; align-items: center; gap: 14px; }
        .stat-card .ic { width: 44px; height: 44px; border-radius: 12px; display: grid; place-items: center; font-size: 19px; flex: 0 0 auto; }
        .stat-card .n { font-size: 21px; font-weight: 800; }
        .stat-card .l { font-size: 12px; color: #7286a3; margin-top: 1px; }
        .ic.blue { background: #e7f0ff; color: #2f7fe0; }
        .ic.gold { background: #faf1de; color: #b78a3a; }
        .ic.green { background: #e9f9ef; color: #22c55e; }
        .ic.red { background: #fdeaea; color: #ef4444; }
        .ic.purple { background: #efeaff; color: #7c5cff; }
        .grid2 { display: grid; grid-template-columns: 1.4fr 1fr; gap: 18px; }
        .panel { background: #fff; border: 1px solid #e7ecf3; border-radius: 16px; padding: 20px; }
        .panel-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 14px; }
        .panel-head h3 { margin: 0; font-size: 15.5px; font-weight: 800; }
        .activity-row { display: flex; align-items: center; gap: 12px; padding: 10px 0; border-bottom: 1px solid #e7ecf3; }
        .activity-row:last-child { border-bottom: 0; }
        .activity-row .ic { width: 34px; height: 34px; border-radius: 9px; display: grid; place-items: center; font-size: 14px; flex: 0 0 auto; }
        .activity-row .t { font-size: 12.5px; font-weight: 600; }
        .activity-row .s { font-size: 11px; color: #7286a3; }
        .mini-cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(120px, 1fr)); gap: 10px; }
        .mini-card { background: #f4f6fa; border-radius: 12px; padding: 12px; text-align: center; }
        .mini-card .name { font-weight: 800; font-size: 14px; }
        .mini-card .sub { font-size: 10.5px; color: #7286a3; margin-top: 2px; }
        .btn {
          border: 1px solid #0f2747; background: #fff; color: #0f2747;
          padding: 9px 16px; border-radius: 10px; font-weight: 700; font-size: 13px;
          cursor: pointer; font-family: 'Cairo', sans-serif;
        }
        .btn.solid { background: linear-gradient(135deg, #0f2747, #15335d); border-color: transparent; color: #fff; }
        .btn.gold { background: linear-gradient(135deg, #d4a853, #b78a3a); border-color: transparent; color: #0f2747; }
        .btn.small { padding: 6px 12px; font-size: 12px; border-radius: 8px; }
        .btn.danger { border-color: #ef4444; color: #ef4444; background: #fff; }
        .btn:hover { filter: brightness(1.05); }
        .building-switch { display: flex; align-items: center; gap: 10px; margin-bottom: 18px; flex-wrap: wrap; }
        .building-switch label { font-size: 12.5px; color: #7286a3; font-weight: 700; }
        .building-switch select {
          padding: 9px 14px; border: 1px solid #e7ecf3; border-radius: 10px; background: #fff;
          font-family: 'Cairo', sans-serif; font-size: 13.5px; font-weight: 700; color: #152238;
        }
        .b-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(230px, 1fr)); gap: 16px; margin-bottom: 20px; }
        .b-card { background: #fff; border: 1px solid #e7ecf3; border-radius: 16px; padding: 18px; }
        .b-card-top { display: flex; justify-content: space-between; align-items: center; }
        .b-card-top h3 { margin: 0; font-size: 18px; font-weight: 800; }
        .b-pill { font-size: 10.5px; font-weight: 700; padding: 3px 10px; border-radius: 14px; background: #e9f9ef; color: #22c55e; }
        .b-stats { display: flex; justify-content: space-between; margin-top: 14px; font-size: 11.5px; color: #7286a3; }
        .b-stats b { display: block; font-size: 15px; color: #152238; }
        .b-bar { height: 7px; background: #eef2f8; border-radius: 99px; overflow: hidden; margin: 10px 0; }
        .b-bar span { display: block; height: 100%; border-radius: 99px; }
        .b-card .pct { font-size: 12px; font-weight: 800; }
        .detail-panel { background: #fff; border: 1px solid #e7ecf3; border-radius: 16px; padding: 20px; margin-top: 6px; }
        .door-stats { display: flex; gap: 14px; margin-bottom: 16px; flex-wrap: wrap; }
        .dstat { flex: 1; min-width: 120px; background: #f4f6fa; border-radius: 12px; padding: 14px 16px; text-align: center; }
        .dstat .n { font-size: 20px; font-weight: 800; color: #0f2747; }
        .dstat .n.rust { color: #ef4444; }
        .dstat .l { color: #7286a3; font-size: 11.5px; margin-top: 2px; }
        .door-controls { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 16px; }
        .door-filters { display: flex; gap: 6px; background: #f4f6fa; padding: 4px; border-radius: 12px; }
        .door-filters span { padding: 6px 14px; border-radius: 9px; font-size: 12.5px; font-weight: 700; color: #7286a3; cursor: pointer; }
        .door-filters span.active { background: #fff; color: #0f2747; box-shadow: 0 2px 6px rgba(20,50,100,.12); }
        .door-controls input { flex: 1; min-width: 180px; padding: 9px 14px; border: 1px solid #e7ecf3; border-radius: 10px; background: #f4f6fa; font-family: 'Cairo', sans-serif; font-size: 13.5px; }
        .doors-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(80px, 1fr)); gap: 9px; }
        .door-tile { border-radius: 12px; padding: 11px 6px; text-align: center; cursor: pointer; background: #e9f9ef; transition: transform .12s; }
        .door-tile:hover { transform: translateY(-2px); }
        .door-tile.empty { background: #fdeaea; }
        .door-tile .dn { font-weight: 800; font-size: 14px; }
        .door-tile .dname { font-size: 10px; color: #7286a3; margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .tbl-wrap { overflow: auto; }
        table { width: 100%; border-collapse: collapse; font-size: 13px; min-width: 600px; }
        th, td { padding: 11px 8px; text-align: center; border-bottom: 1px solid #e7ecf3; }
        th { color: #7286a3; font-weight: 700; font-size: 11.5px; }
        td.name { text-align: right; font-weight: 700; }
        .pill { display: inline-block; padding: 3px 11px; border-radius: 20px; font-weight: 700; font-size: 11px; }
        .pill.paid { background: #e9f9ef; color: #22c55e; }
        .pill.partial { background: #fef3e0; color: #f59e0b; }
        .pill.unpaid { background: #fdeaea; color: #ef4444; }
        .chip { font-size: 10.5px; padding: 3px 9px; border-radius: 20px; background: #f4f6fa; color: #7286a3; }
        .chip.paid { background: #e9f9ef; color: #22c55e; }
        .chip.partial { background: #fef3e0; color: #f59e0b; }
        .chip.unpaid, .chip.missing { background: #fdeaea; color: #ef4444; }
        .proj-row { padding: 13px 0; border-bottom: 1px solid #e7ecf3; }
        .proj-row:last-child { border-bottom: 0; }
        .proj-top { display: flex; justify-content: space-between; align-items: baseline; gap: 10px; }
        .proj-name { font-weight: 700; }
        .proj-amt { color: #0f2747; font-weight: 800; }
        .proj-target { color: #7286a3; font-size: 11.5px; margin-top: 2px; }
        .proj-bar { height: 6px; background: #f4f6fa; border-radius: 99px; overflow: hidden; margin-top: 8px; }
        .proj-bar span { display: block; height: 100%; background: linear-gradient(90deg, #d4a853, #b78a3a); }
        .sup-row { display: flex; justify-content: space-between; align-items: center; padding: 10px 0; border-bottom: 1px solid #e7ecf3; }
        .sup-row:last-child { border-bottom: 0; }
        .sup-name { font-weight: 700; font-size: 13.5px; }
        .sup-role { color: #7286a3; font-size: 11.5px; }
        .soon-box { background: #fff; border: 1px dashed #e7ecf3; border-radius: 16px; padding: 60px 20px; text-align: center; color: #7286a3; }
        .soon-box .ic { font-size: 34px; margin-bottom: 10px; }
        .soon-box h3 { color: #152238; margin: 0 0 6px; }
        .overlay { display: none; position: fixed; inset: 0; background: rgba(15,39,71,.5); place-items: center; z-index: 30; }
        .overlay.open { display: grid; }
        .modal { width: min(440px, 92vw); background: #fff; border-radius: 16px; padding: 22px; box-shadow: 0 24px 60px -20px rgba(15,39,71,.5); }
        .modal h3 { margin: 0 0 14px; font-size: 16px; font-weight: 800; border-bottom: 1px solid #e7ecf3; padding-bottom: 12px; }
        .field { margin-bottom: 12px; }
        .field label { display: block; font-size: 12px; color: #7286a3; margin-bottom: 5px; font-weight: 600; }
        .field input, .field select { width: 100%; padding: 10px 12px; border: 1px solid #e7ecf3; border-radius: 10px; background: #f4f6fa; font-family: 'Cairo', sans-serif; font-size: 13.5px; color: #152238; }
        .field input:focus, .field select:focus { outline: 2px solid #d4a853; background: #fff; }
        .modal-actions { display: flex; gap: 10px; margin-top: 16px; }
        .rv-head { display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #e7ecf3; padding-bottom: 12px; margin-bottom: 14px; }
        .rv-head h3 { margin: 0; border: 0; padding: 0; }
        .rv-close { background: none; border: none; font-size: 19px; color: #7286a3; cursor: pointer; }
        .rv-row { display: flex; align-items: center; gap: 12px; padding: 9px 0; border-bottom: 1px solid #e7ecf3; }
        .rv-row:last-of-type { border-bottom: 0; }
        .rv-row .l { font-size: 11px; color: #7286a3; }
        .rv-row .v { font-weight: 700; font-size: 14px; }
        .rv-projects { margin-top: 12px; }
        .rv-projects .l { font-size: 11px; color: #7286a3; margin-bottom: 8px; }
        .rv-delete { margin-top: 16px; width: 100%; background: #fdeaea; color: #ef4444; border: none; padding: 10px; border-radius: 10px; font-weight: 700; font-family: 'Cairo', sans-serif; cursor: pointer; }
        .sidebar-backdrop { display: none; position: fixed; inset: 0; background: rgba(15,39,71,.45); z-index: 35; }
        .sidebar-backdrop.open { display: block; }
        @media(max-width: 900px) { .grid2 { grid-template-columns: 1fr; } }
        @media(max-width: 820px) {
          .sidebar { position: fixed; right: -260px; z-index: 40; transition: right .2s; box-shadow: 0 0 40px rgba(0,0,0,.3); }
          .sidebar.open { right: 0; }
          .hamburger { display: block; }
          .topbar .date { display: none; }
          .content { padding: 16px; }
        }
        @media(max-width: 480px) {
          .stat-card { padding: 12px 14px; }
          .door-stats { flex-direction: column; }
          .modal { padding: 18px; }
        }
      `}</style>

      {/* Sidebar Backdrop */}
      <div
        className={`sidebar-backdrop${sidebarOpen ? ' open' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Sidebar */}
      <aside className={`sidebar${sidebarOpen ? ' open' : ''}`}>
        <div className="brand">
          <div className="mark">س</div>
          <h1>سجلّ مساهمات السكن</h1>
          <p>معًا نحو حيّ أفضل</p>
        </div>
        <div className="nav-group">
          <div className={`nav-item${activePage === 'dashboard' ? ' active' : ''}`} onClick={() => showPage('dashboard')}><span className="ic">🏠</span> لوحة القيادة</div>
          <div className={`nav-item${activePage === 'buildings' ? ' active' : ''}`} onClick={() => showPage('buildings')}><span className="ic">🏢</span> العمارات</div>
          <div className={`nav-item${activePage === 'residents' ? ' active' : ''}`} onClick={() => showPage('residents')}><span className="ic">👥</span> السكان</div>
          <div className={`nav-item${activePage === 'projects' ? ' active' : ''}`} onClick={() => showPage('projects')}><span className="ic">📊</span> المشاريع</div>
          <div className={`nav-item${activePage === 'contributions' ? ' active' : ''}`} onClick={() => showPage('contributions')}><span className="ic">💰</span> المساهمات والتحصيل</div>
        </div>
        <div className="nav-group">
          <div className="nav-item soon"><span className="ic">💼</span> المصاريف <span className="tag">قريبًا</span></div>
          <div className="nav-item soon"><span className="ic">🔧</span> الصيانة والشكاوى <span className="tag">قريبًا</span></div>
          <div className="nav-item soon"><span className="ic">📢</span> الإعلانات <span className="tag">قريبًا</span></div>
          <div className="nav-item soon"><span className="ic">📄</span> التقارير <span className="tag">قريبًا</span></div>
          <div className={`nav-item${activePage === 'settings' ? ' active' : ''}`} onClick={() => showPage('settings')}><span className="ic">⚙️</span> الإعدادات</div>
        </div>
        <div className="sidebar-footer">© 2026 سجلّ مساهمات السكن</div>
      </aside>

      {/* Main */}
      <div className="main">
        {/* Topbar */}
        <div className="topbar">
          <button className="hamburger" onClick={() => setSidebarOpen(!sidebarOpen)}>☰</button>
          <div className="date">📅 <span>{currentDate}</span></div>
          <input className="search" placeholder="البحث في النظام..." disabled />
          <div className="spacer" />
          <div className="bell">🔔<span className="dot">{notifCount}</span></div>
          <div className="user">
            <div className="av">مد</div>
            <div><div className="name">مدير النظام</div><div className="role">مرحبًا بك</div></div>
          </div>
        </div>

        {/* Content */}
        <div className="content">

          {/* ===== Dashboard ===== */}
          <section className={`page${activePage === 'dashboard' ? ' active' : ''}`}>
            <div className="page-head">
              <h2>لوحة القيادة</h2>
              <p>نظرة شاملة على حالة الإقامة والمشاريع والتحصيل</p>
            </div>
            <div className="stat-grid">
              <div className="stat-card"><div className="ic gold">🏢</div><div><div className="n">{keys.length}</div><div className="l">عدد العمارات</div></div></div>
              <div className="stat-card"><div className="ic blue">👥</div><div><div className="n">{dashStats.totalReg}</div><div className="l">السكان المسجَّلون</div></div></div>
              <div className="stat-card"><div className="ic purple">📊</div><div><div className="n">{dashStats.totalProjects}</div><div className="l">المشاريع النشطة</div></div></div>
              <div className="stat-card"><div className="ic green">💰</div><div><div className="n">{fmt(dashStats.totalCollected)} دج</div><div className="l">إجمالي المحصَّل</div></div></div>
            </div>
            <div className="grid2">
              <div className="panel">
                <div className="panel-head">
                  <h3>نظرة سريعة على العمارات</h3>
                  <span className="btn small" style={{cursor:'pointer'}} onClick={() => showPage('buildings')}>عرض الكل</span>
                </div>
                <div className="mini-cards">
                  {keys.map(k => {
                    const s = summary(k);
                    return (
                      <div key={k} className="mini-card">
                        <div className="name">{k}</div>
                        <div className="sub">{s.participants}/{s.apartments} · {s.coverage.toFixed(0)}%</div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="panel">
                <div className="panel-head"><h3>آخر النشاطات</h3></div>
                {activity.length === 0 ? (
                  <p className="proj-target">لا توجد نشاطات بعد.</p>
                ) : (
                  activity.map((a, i) => (
                    <div key={i} className="activity-row">
                      <div className="ic gold">{a.icon}</div>
                      <div>
                        <div className="t">{a.text}</div>
                        <div className="s">{a.time.getHours().toString().padStart(2,'0')}:{a.time.getMinutes().toString().padStart(2,'0')}</div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </section>

          {/* ===== Buildings ===== */}
          <section className={`page${activePage === 'buildings' ? ' active' : ''}`}>
            <div className="page-head">
              <h2>إدارة العمارات</h2>
              <p>متابعة العمارات ونسب التسجيل والإشغال</p>
            </div>
            <button className="btn gold" style={{marginBottom:16}} onClick={() => setModalBuilding(true)}>+ إضافة عمارة</button>
            <div className="b-grid">
              {keys.map(k => {
                const s = summary(k);
                const color = s.coverage >= 40 ? '#22c55e' : (s.coverage >= 20 ? '#f59e0b' : '#ef4444');
                return (
                  <div key={k} className="b-card">
                    <div className="b-card-top"><h3>{k}</h3><span className="b-pill">نشطة</span></div>
                    <div className="b-stats">
                      <div>إجمالي الشقق<b>{s.apartments}</b></div>
                      <div>المسجَّلة<b>{s.participants}</b></div>
                      <div>المتبقية<b>{s.apartments - s.participants}</b></div>
                    </div>
                    <div className="b-bar"><span style={{width:`${s.coverage}%`, background:color}} /></div>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                      <span className="pct" style={{color}}>{s.coverage.toFixed(0)}%</span>
                      <button className="btn small" onClick={() => { setCurrent(k); showPage('residents'); }}>عرض التفاصيل</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* ===== Residents ===== */}
          <section className={`page${activePage === 'residents' ? ' active' : ''}`}>
            <div className="page-head">
              <h2>إدارة السكان</h2>
              <p>قائمة السكان المسجَّلين وبيانات السكن والتواصل</p>
            </div>
            <div className="building-switch">
              <label>العمارة:</label>
              <select value={current} onChange={e => setCurrent(e.target.value)}>
                {keys.map(k => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
            <div className="door-stats">
              <div className="dstat"><div className="n rust">{b.apartments - registeredCount}</div><div className="l">لم يسجّل</div></div>
              <div className="dstat"><div className="n">{registeredCount}</div><div className="l">مسجّل</div></div>
              <div className="dstat"><div className="n">{b.apartments}</div><div className="l">إجمالي الشقق</div></div>
            </div>
            <div className="door-controls">
              <div className="door-filters">
                <span data-f="all" className={doorsFilter === 'all' ? 'active' : ''} onClick={() => setDoorsFilter('all')}>الكل</span>
                <span data-f="registered" className={doorsFilter === 'registered' ? 'active' : ''} onClick={() => setDoorsFilter('registered')}>مسجّل</span>
                <span data-f="unregistered" className={doorsFilter === 'unregistered' ? 'active' : ''} onClick={() => setDoorsFilter('unregistered')}>لم يسجّل</span>
              </div>
              <input type="text" value={doorSearch} onChange={e => setDoorSearch(e.target.value)} placeholder="ابحث بالاسم أو رقم الشقة..." />
              <button className="btn small">⭳ تصدير CSV</button>
            </div>
            <div className="doors-grid">
              {doorTiles.length === 0 ? (
                <p className="proj-target">لا توجد نتائج مطابقة.</p>
              ) : doorTiles.map(({ apt, r, isReg }) => (
                <div
                  key={apt}
                  className={`door-tile${isReg ? '' : ' empty'}`}
                  onClick={() => {
                    if (isReg) { setViewingApt(apt); setModalResidentView(true); }
                    else { openAddResident(apt); }
                  }}
                >
                  <div>{isReg ? '✅' : '❌'}</div>
                  <div className="dn">{String(apt).padStart(2, '0')}</div>
                  {isReg && r && <div className="dname">{r.name.split(' ')[0]}</div>}
                </div>
              ))}
            </div>
          </section>

          {/* ===== Projects ===== */}
          <section className={`page${activePage === 'projects' ? ' active' : ''}`}>
            <div className="page-head">
              <h2>مشاريع الحي</h2>
              <p>إنشاء المشاريع ومتابعة أهدافها وتحصيلها</p>
            </div>
            <div className="building-switch">
              <label>العمارة:</label>
              <select value={current} onChange={e => setCurrent(e.target.value)}>
                {keys.map(k => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
            <div className="panel">
              <div className="panel-head">
                <h3>سجلّ المشاريع</h3>
                <button className="btn small gold" onClick={() => setModalProject(true)}>+ إضافة مشروع</button>
              </div>
              {b.projects.length === 0 ? (
                <p className="proj-target">لا توجد مشاريع بعد.</p>
              ) : b.projects.map(p => {
                const collected = b.contributions.filter(c => c.projectId === p.id).reduce((s, c) => s + c.amount, 0);
                const pct = p.target ? Math.min(100, collected / p.target * 100) : null;
                return (
                  <div key={p.id} className="proj-row">
                    <div className="proj-top">
                      <div className="proj-name">{p.name}</div>
                      <div style={{display:'flex',alignItems:'center',gap:10}}>
                        <div className="proj-amt">{fmt(collected)} دج</div>
                        <button className="btn small danger" onClick={() => deleteProject(p.id)}>حذف</button>
                      </div>
                    </div>
                    <div className="proj-target">
                      {p.target ? `الهدف: ${fmt(p.target)} دج` : 'بدون هدف مالي محدَّد'}
                      {p.unitPrice ? ` · سعر الوحدة: ${fmt(p.unitPrice)} دج` : ''}
                      {p.due ? ` · المطلوب من كل ساكن: ${fmt(p.due)} دج` : ''}
                    </div>
                    {pct !== null && (
                      <div className="proj-bar"><span style={{width:`${pct}%`}} /></div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* ===== Contributions ===== */}
          <section className={`page${activePage === 'contributions' ? ' active' : ''}`}>
            <div className="page-head">
              <h2>المساهمات والتحصيل</h2>
              <p>متابعة مساهمات السكان حسب المشاريع وحالة السداد</p>
            </div>
            <div className="building-switch">
              <label>العمارة:</label>
              <select value={current} onChange={e => setCurrent(e.target.value)}>
                {keys.map(k => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
            <div className="panel">
              <div className="panel-head">
                <h3>سجلّ التحصيلات</h3>
                <button className="btn small solid" onClick={openAddContribution}>+ تسجيل مساهمة</button>
              </div>
              <div className="tbl-wrap">
                <table>
                  <thead><tr><th>الساكن</th><th>الشقة</th><th>المشروع</th><th>المبلغ</th><th>الحالة</th><th>تعديل</th><th>حذف</th></tr></thead>
                  <tbody>
                    {getContribsSorted().map(c => {
                      const proj = b.projects.find(p => p.id === c.projectId);
                      const projName = proj ? proj.name : '—';
                      return (
                        <tr key={c.id}>
                          <td className="name">{c.name}</td>
                          <td>{c.apt}</td>
                          <td>{projName}</td>
                          <td>
                            {fmt(c.amount)} دج
                            {c.qty && c.qty > 1 && <span style={{color:'#7286a3',fontWeight:400}}> ({c.qty}×{fmt(c.amount/c.qty)})</span>}
                            {proj && proj.due && c.amount < proj.due && (
                              <><br /><span style={{color:'#ef4444',fontSize:11}}>الباقي: {fmt(proj.due - c.amount)} دج</span></>
                            )}
                          </td>
                          <td><span className={`pill ${c.status}`}>{statusLabel[c.status]}</span></td>
                          <td><button className="btn small" onClick={() => editContribution(c.id)}>تعديل</button></td>
                          <td><button className="btn small danger" onClick={() => deleteContribution(c.id)}>حذف</button></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {(() => {
                const unregistered = b.apartments - new Set(b.contributions.map(c => c.apt)).size;
                return (
                  <p className="proj-target" style={{marginTop:10}}>
                    {unregistered > 0
                      ? `${unregistered} شقة من أصل ${b.apartments} لا تملك أي مساهمة مسجَّلة بعد.`
                      : `جميع شقق العمارة ${current} لها مساهمة مسجَّلة.`
                    }
                  </p>
                );
              })()}
            </div>
          </section>

          {/* ===== Coming Soon Pages ===== */}
          <section className={`page${activePage === 'expenses' ? ' active' : ''}`}>
            <div className="page-head"><h2>المصاريف</h2></div>
            <div className="soon-box"><div className="ic">💼</div><h3>وحدة المصاريف قيد التطوير</h3><p>ستتيح تسجيل مصاريف الصيانة والمقاولين ومتابعة رصيد الخزينة.</p></div>
          </section>
          <section className={`page${activePage === 'maintenance' ? ' active' : ''}`}>
            <div className="page-head"><h2>الصيانة والشكاوى</h2></div>
            <div className="soon-box"><div className="ic">🔧</div><h3>وحدة الصيانة قيد التطوير</h3><p>ستتيح استقبال طلبات الأعطال ومتابعتها حتى الحل.</p></div>
          </section>
          <section className={`page${activePage === 'announcements' ? ' active' : ''}`}>
            <div className="page-head"><h2>الإعلانات</h2></div>
            <div className="soon-box"><div className="ic">📢</div><h3>وحدة الإعلانات قيد التطوير</h3><p>ستتيح نشر التبليغات والتذكيرات لجميع السكان.</p></div>
          </section>
          <section className={`page${activePage === 'reports' ? ' active' : ''}`}>
            <div className="page-head"><h2>التقارير</h2></div>
            <div className="soon-box"><div className="ic">📄</div><h3>وحدة التقارير قيد التطوير</h3><p>ستتيح تصدير تقارير أداء وتحصيل شاملة بصيغة Excel وPDF.</p></div>
          </section>

          {/* ===== Settings ===== */}
          <section className={`page${activePage === 'settings' ? ' active' : ''}`}>
            <div className="page-head">
              <h2>الإعدادات</h2>
              <p>إدارة المشرفين والعمارات</p>
            </div>
            <div className="building-switch">
              <label>العمارة:</label>
              <select value={current} onChange={e => setCurrent(e.target.value)}>
                {keys.map(k => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
            <div className="grid2">
              <div className="panel">
                <div className="panel-head">
                  <h3>المشرفون</h3>
                  <button className="btn small gold" onClick={() => setModalSupervisor(true)}>+ إضافة مشرف</button>
                </div>
                {b.supervisors.length === 0 ? (
                  <p className="proj-target">لا يوجد مشرفون مسجَّلون بعد.</p>
                ) : b.supervisors.map((s, i) => (
                  <div key={i} className="sup-row">
                    <div>
                      <div className="sup-name">{s.name}</div>
                      <div className="sup-role">{s.role} · {s.contact}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="panel">
                <div className="panel-head">
                  <h3>العمارات</h3>
                  <button className="btn small" onClick={() => setModalBuilding(true)}>+ إضافة عمارة</button>
                </div>
                <p className="proj-target">لإدارة أو حذف عمارة كاملة، انتقل إلى صفحة «العمارات».</p>
              </div>
            </div>
          </section>

        </div>
      </div>

      {/* ===== Modal: Add Building ===== */}
      <div className={`overlay${modalBuilding ? ' open' : ''}`}>
        <div className="modal">
          <h3>إضافة عمارة جديدة</h3>
          <div className="field"><label>اسم العمارة (مثال: A2)</label><input value={bName} onChange={e => setBName(e.target.value)} /></div>
          <div className="field"><label>عدد الشقق</label><input type="number" min={1} value={bApts} onChange={e => setBApts(e.target.value)} /></div>
          <div className="modal-actions">
            <button className="btn solid" onClick={submitBuilding}>حفظ</button>
            <button className="btn" onClick={() => setModalBuilding(false)}>إلغاء</button>
          </div>
        </div>
      </div>

      {/* ===== Modal: Add Project ===== */}
      <div className={`overlay${modalProject ? ' open' : ''}`}>
        <div className="modal">
          <h3>إضافة مشروع جديد</h3>
          <div className="field"><label>اسم المشروع</label><input value={pName} onChange={e => setPName(e.target.value)} /></div>
          <div className="field"><label>المبلغ المطلوب من كل ساكن (دج) — اختياري</label><input type="number" min={0} placeholder="مثال: 1000" value={pDue} onChange={e => setPDue(e.target.value)} /></div>
          <div className="field"><label>سعر الوحدة (دج) — اختياري</label><input type="number" min={0} placeholder="مثال: 300" value={pUnitPrice} onChange={e => setPUnitPrice(e.target.value)} /></div>
          <div className="field"><label>الهدف المالي الكلي (دج) — اختياري</label><input type="number" min={0} value={pTarget} onChange={e => setPTarget(e.target.value)} /></div>
          <div className="modal-actions">
            <button className="btn solid" onClick={submitProject}>حفظ</button>
            <button className="btn" onClick={() => setModalProject(false)}>إلغاء</button>
          </div>
        </div>
      </div>

      {/* ===== Modal: Add Supervisor ===== */}
      <div className={`overlay${modalSupervisor ? ' open' : ''}`}>
        <div className="modal">
          <h3>إضافة مشرف</h3>
          <div className="field"><label>الاسم الكامل</label><input value={sName} onChange={e => setSName(e.target.value)} /></div>
          <div className="field"><label>الصفة</label>
            <select value={sRole} onChange={e => setSRole(e.target.value)}>
              <option>مشرف عمارة</option>
              <option>مدير عام</option>
              <option>أمين صندوق</option>
            </select>
          </div>
          <div className="field"><label>رقم الهاتف أو البريد</label><input value={sContact} onChange={e => setSContact(e.target.value)} /></div>
          <div className="modal-actions">
            <button className="btn solid" onClick={submitSupervisor}>حفظ</button>
            <button className="btn" onClick={() => setModalSupervisor(false)}>إلغاء</button>
          </div>
        </div>
      </div>

      {/* ===== Modal: Add/Edit Contribution ===== */}
      <div className={`overlay${modalContribution ? ' open' : ''}`}>
        <div className="modal">
          <h3>{contribModalTitle}</h3>
          <div className="field">
            <label>الساكن (من قائمة المسجَّلين)</label>
            <select value={cResident} onChange={e => setCResident(e.target.value)}>
              {getResidentsForSelect().length === 0
                ? <option value="">لا يوجد سكان مسجَّلون بعد</option>
                : getResidentsForSelect().map(r => <option key={r.apt} value={r.apt}>{r.name} — شقة {r.apt}</option>)
              }
            </select>
          </div>
          <div className="field">
            <label>المشروع</label>
            <select value={cProject} onChange={e => handleContribProjectChange(e.target.value)}>
              {getProjectsForSelect().map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          {showQtyField && (
            <div className="field">
              <label>الكمية (سعر الوحدة: <span>{cUnitPriceLabel}</span> دج)</label>
              <input type="number" min={1} value={cQty} onChange={e => { setCQty(e.target.value); const b2 = getBuilding(); const proj = b2.projects.find(p => p.id === cProject); if (proj && proj.unitPrice) { setCAmount(String(proj.unitPrice * (parseInt(e.target.value) || 1))); } }} />
            </div>
          )}
          {showDueField && (
            <div className="field">
              <label>المبلغ المطلوب لهذا المشروع</label>
              <input value={cDueDisplay} disabled />
            </div>
          )}
          <div className="field">
            <label>{cAmountLabel}</label>
            <input type="number" min={0} value={cAmount} onChange={e => handleContribAmountInput(e.target.value)} />
          </div>
          {showRemainingField && (
            <div className="field">
              <label style={{color:'#ef4444'}}>الباقي المتبقّي</label>
              <input value={cRemainingDisplay} disabled />
            </div>
          )}
          <div className="field">
            <label>الحالة</label>
            <select value={cStatus} onChange={e => setCStatus(e.target.value as 'paid' | 'partial' | 'unpaid')}>
              <option value="paid">مسدد</option>
              <option value="partial">جزئي</option>
              <option value="unpaid">غير مسدد</option>
            </select>
          </div>
          <div className="modal-actions">
            <button className="btn solid" onClick={submitContribution}>حفظ</button>
            <button className="btn" onClick={() => { setEditingContribId(null); setModalContribution(false); }}>إلغاء</button>
          </div>
        </div>
      </div>

      {/* ===== Modal: Add Resident ===== */}
      <div className={`overlay${modalResident ? ' open' : ''}`}>
        <div className="modal">
          <h3>{residentModalTitle}</h3>
          <div className="field"><label>الاسم الكامل</label><input value={rName} onChange={e => setRName(e.target.value)} /></div>
          <div className="field"><label>رقم الشقة</label><input type="number" min={1} value={rApt} onChange={e => setRApt(e.target.value)} /></div>
          <div className="field"><label>رقم الهاتف</label><input value={rPhone} onChange={e => setRPhone(e.target.value)} /></div>
          <div className="field"><label>تاريخ التسجيل</label><input type="date" value={rDate} onChange={e => setRDate(e.target.value)} /></div>
          <div className="modal-actions">
            <button className="btn solid" onClick={submitResident}>حفظ</button>
            <button className="btn" onClick={() => setModalResident(false)}>إلغاء</button>
          </div>
        </div>
      </div>

      {/* ===== Modal: View Resident ===== */}
      <div className={`overlay${modalResidentView ? ' open' : ''}`}>
        <div className="modal">
          {viewingResident && viewingApt !== null && (() => {
            const projContribs = b.contributions.filter(c => c.apt === viewingApt);
            let totalProjects = b.projects.length;
            const completed = b.projects.filter(p => {
              const c = projContribs.find(x => x.projectId === p.id);
              return c && c.status === 'paid';
            }).length;
            return (
              <>
                <div className="rv-head">
                  <h3>عمارة {current} — باب {String(viewingApt).padStart(2, '0')}</h3>
                  <button className="rv-close" onClick={() => setModalResidentView(false)}>✕</button>
                </div>
                <span className="pill paid">مسجّل</span>
                <div className="rv-row"><div>👤</div><div><div className="l">الاسم الكامل</div><div className="v">{viewingResident.name}</div></div></div>
                <div className="rv-row"><div>📞</div><div><div className="l">رقم الهاتف</div><div className="v">{viewingResident.phone}</div></div></div>
                <div className="rv-row"><div>📅</div><div><div className="l">تاريخ التسجيل</div><div className="v">{viewingResident.registeredAt}</div></div></div>
                <div className="rv-projects">
                  <div className="l">إكمال المشاريع — {completed} / {totalProjects}</div>
                  <div>
                    {b.projects.length === 0
                      ? <span className="chip missing">لا توجد مشاريع بعد</span>
                      : b.projects.map(p => {
                          const c = projContribs.find(x => x.projectId === p.id);
                          const st = c ? c.status : 'missing';
                          const label = st === 'missing' ? 'لم يسدَّد بعد' : statusLabel[st];
                          return <span key={p.id} className={`chip ${st}`} style={{marginLeft:4}}>{p.name}: {label}</span>;
                        })
                    }
                  </div>
                </div>
                <button className="rv-delete" onClick={() => deleteResident(viewingApt)}>🗑 حذف هذا التسجيل</button>
              </>
            );
          })()}
        </div>
      </div>
    </>
  );
}
